import {
    SimpleGrid,
    ModalFooter,
    ModalHeader,
    ModalBody,
    BlankModal,
    ButtonGroup,
    useToast,
    classnames,
    Flex,
    Button,
    Text,
    Box,
} from '@cad-arc';
import { UpdateToEventEntity, UpdateToEventView } from '@mark43/cad-api';
import { EntitiesToUpdate, StylesOverride } from './types';
import React, { useCallback, useState } from 'react';
import { useUpdateEventEntities } from './use-update-event-entities';
import { errorOccurredWhile } from '../../common/helpers/error-occurred-while';

const Entity = ({
    entity,
    testId,
    nullEntityLabel,
}: {
    entity: UpdateToEventEntity | null;
    testId?: string;
    nullEntityLabel?: string;
}) => {
    if (entity === null) {
        return (
            <Box data-testid={testId}>
                {nullEntityLabel && <Text variant="bodyMd">{nullEntityLabel}</Text>}
            </Box>
        );
    }
    return (
        <Box data-testid={testId}>
            <Text fontWeight="bold" variant="bodyMd">
                {entity.title}
            </Text>
            {entity.updateToEventProperties.map(({ name, display }) => (
                <Flex key={name} paddingLeft={2} gap={1}>
                    <Text variant="bodyMd" color="tertiary">
                        {name}:
                    </Text>
                    <Text variant="bodyMd">{display}</Text>
                </Flex>
            ))}
        </Box>
    );
};

export const UpdateToEventConfirmModal = ({
    closeConfirmModal,
    entitiesToUpdate,
    isConfirmModalOpen,
    updateToEventView,
    onUpdateToEventSuccess,
    stylesOverride,
}: {
    closeConfirmModal: () => void;
    entitiesToUpdate?: EntitiesToUpdate;
    isConfirmModalOpen: boolean;
    updateToEventView: UpdateToEventView;
    onUpdateToEventSuccess: () => void;
    stylesOverride: StylesOverride;
}) => {
    const { updateEventEntities } = useUpdateEventEntities();
    const { eventId, parsedVehicle, parsedPerson } = updateToEventView;
    const { vehicle: eventVehicle, person: eventPerson } = entitiesToUpdate || {};
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    const toast = useToast();

    const onConfirm = useCallback(async () => {
        try {
            setIsUpdating(true);
            await updateEventEntities(
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                eventId!,
                eventPerson === null ? undefined : eventPerson,
                eventVehicle === null ? undefined : eventVehicle,
                eventPerson === undefined ? undefined : parsedPerson,
                eventVehicle === undefined ? undefined : parsedVehicle
            );
            closeConfirmModal();
            onUpdateToEventSuccess();
        } catch (error) {
            toast({
                status: 'error',
                description: errorOccurredWhile(`updating fields on event #${eventId}`),
            });
        } finally {
            setIsUpdating(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateToEventView, entitiesToUpdate, updateEventEntities]);

    return (
        <BlankModal
            isCentered
            isOpen={isConfirmModalOpen}
            onClose={closeConfirmModal}
            size="md"
            className={classnames('ignore-react-onclickoutside', stylesOverride['modal'])}
        >
            <ModalHeader>
                <Text as="h2" variant="headingMd">
                    Update To Event
                </Text>
            </ModalHeader>
            <ModalBody>
                <Text variant="bodyMd">
                    The following fields will be updated on event
                    <Text as="span" fontWeight="bold">
                        {' '}
                        #{updateToEventView.eventNumber}
                    </Text>
                    :
                </Text>
                <br />
                <SimpleGrid columns={2} spacing={11}>
                    <Text variant="headingSm">Existing Fields</Text>
                    <Text variant="headingSm">New Fields</Text>

                    {eventPerson !== undefined && parsedPerson !== undefined ? (
                        <>
                            <Entity
                                entity={eventPerson}
                                testId="existing-person-field"
                                nullEntityLabel="Person does not exist on this event"
                            />
                            <Entity entity={parsedPerson} testId="new-person-field" />
                        </>
                    ) : undefined}

                    {eventVehicle !== undefined && parsedVehicle !== undefined ? (
                        <>
                            <Entity
                                entity={eventVehicle}
                                testId="existing-vehicle-field"
                                nullEntityLabel="Vehicle does not exist on this event"
                            />
                            <Entity entity={parsedVehicle} testId="new-vehicle-field" />
                        </>
                    ) : undefined}
                </SimpleGrid>
            </ModalBody>
            <ModalFooter>
                <ButtonGroup className={stylesOverride['button-group']}>
                    <Button
                        variant="ghost"
                        onClick={closeConfirmModal}
                        className={stylesOverride['button-ghost']}
                    >
                        Cancel
                    </Button>
                    <Button
                        data-testid="confirm-modal-update-button"
                        variant="solid"
                        isLoading={isUpdating}
                        onClick={onConfirm}
                        className={stylesOverride['button-solid']}
                    >
                        Update
                    </Button>
                </ButtonGroup>
            </ModalFooter>
        </BlankModal>
    );
};
